import React, { useEffect, useState } from 'react'
import { Box, Button, Checkbox, Divider, FormControlLabel, TextField, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Modal, SummaryTable } from 'src/ui'
import { formatNumber } from 'src/common/helpers'
import { EarningStatement } from 'src/common/api'
import { formatRate, QCAD } from '../../utils'
import CopyButton from '../CopyButton/CopyButton'

interface ClaimModalProps {
  open: boolean
  statement: EarningStatement | null
  isClaiming: boolean
  isSavingComment?: boolean
  onClose: () => void
  onSubmit: (statement: EarningStatement) => void
  onSaveComment?: (statement: EarningStatement, comments: string) => void
}

const ClaimModal: React.FC<ClaimModalProps> = ({
  open,
  statement,
  isClaiming,
  isSavingComment = false,
  onClose,
  onSubmit,
  onSaveComment
}) => {
  const [acknowledged, setAcknowledged] = useState(false)
  const [comments, setComments] = useState('')

  // Reset the confirmation + comment whenever a new statement is opened.
  useEffect(() => {
    if (open) {
      setAcknowledged(false)
      setComments(statement?.comments ?? '')
    }
  }, [open, statement?.id, statement?.comments])

  if (!statement) return null

  const commentChanged = comments !== (statement.comments ?? '')
  const busy = isClaiming || isSavingComment

  const { revSharingWallet } = statement

  return (
    <Modal open={open} onClose={onClose} title="Mint Earnings" maxWidth="sm">
      <Divider sx={{ mb: 2 }} />

      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
          mb: 1
        }}
      >
        Statement Summary
      </Typography>
      <Box
        sx={{ backgroundColor: '#f8fafc', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, mb: 3 }}
      >
        <SummaryTable
          data={[
            { key: 'Statement Period', value: <strong>{statement.period}</strong> },
            { key: 'Statement Date', value: statement.statementDate },
            {
              key: 'Avg. Daily Balance',
              value: (
                <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{`${formatNumber(
                  statement.avgDailyBalanceQcad
                )} ${QCAD}`}</span>
              )
            },
            {
              key: 'Net Rate',
              value: (
                <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{formatRate(statement.effectiveRate)}</span>
              )
            }
          ]}
        />
      </Box>

      <Box
        sx={{
          backgroundColor: 'rgba(38, 172, 193, 0.06)',
          border: '1px solid',
          borderColor: 'primary.main',
          borderRadius: 1,
          p: 2,
          mb: 3
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: 'primary.main',
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
            mb: 1.5
          }}
        >
          Settlement Instructions
        </Typography>

        <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Settlement Method</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>QCAD Mint</Typography>
        </Box>
        <Divider />

        <Typography sx={{ fontSize: 13, color: 'text.secondary', pt: 1 }}>Rev. Sharing Wallet</Typography>
        <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Name</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{revSharingWallet.name}</Typography>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Address</Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{revSharingWallet.address}</Typography>
            <CopyButton value={revSharingWallet.address} title="Copy wallet address" />
          </Box>
        </Box>
        <Divider />

        <Box display="flex" justifyContent="space-between" alignItems="center" pt={1.25}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>QCAD Amount to Mint</Typography>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 800, color: 'primary.main' }}>
            {`${formatNumber(statement.netRevenuePayoutQcad)} ${QCAD}`}
          </Typography>
        </Box>
      </Box>

      {onSaveComment && (
        <Box sx={{ mb: 3 }}>
          <TextField
            label="Comment"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            size="small"
            placeholder="Add a note for this settlement (saved without minting)"
            disabled={isSavingComment}
          />
          <Box display="flex" justifyContent="flex-end" mt={1}>
            <Button
              variant="outlined"
              size="small"
              disabled={!commentChanged || busy}
              loading={isSavingComment}
              onClick={() => onSaveComment(statement, comments)}
            >
              Save Comment
            </Button>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          backgroundColor: '#f8fafc',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1.5,
          mb: 2
        }}
      >
        <FormControlLabel
          control={<Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />}
          label={
            <Typography sx={{ fontSize: 13 }}>
              I confirm the above earnings amount and authorise the mint order to be issued to the registered wallet
              address.
            </Typography>
          }
          sx={{ alignItems: 'flex-start' }}
        />
      </Box>
      <Box display="flex" justifyContent="flex-end" gap={1.5}>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<CheckCircleIcon />}
          disabled={!acknowledged || busy}
          loading={isClaiming}
          onClick={() => onSubmit(statement)}
        >
          Mint Earnings
        </Button>
      </Box>
    </Modal>
  )
}

export default ClaimModal
